import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./../NcdForm.css";
import Loader from "../../../../components/Loader";
import { ncdAPI } from "../../../../services/ncd.api";
import { formatToApiDate, formatToInputDate } from "../../../../helpers/common-helper";
import {
  Button,
  FormControl,
  Box,
  Typography,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';


import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { FACILITY_MAP, getFacilityLabel } from "../../utils/constants";

// Import Tab Subcomponents
import { CervicalExaminationTab } from "./CervicalExaminationTab";
import { CervicalDiagnosisTab } from "./CervicalDiagnosisTab";
import { CervicalReviewTab } from "./CervicalReviewTab";
import { CervicalFollowupTab } from "./CervicalFollowupTab";
import { CervicalReferTab } from "./CervicalReferTab";
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const CervicalScreening = ({ individualId, cervicalScreeningId, viewMode }) => {

  const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")) || {};
  const today = new Date().toISOString().split("T")[0];
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [cervicalData, setCervicalData] = useState(null);

  const [points, setPoints] = useState([]);
  const [activePoints, setActivePoints] = useState(Array(12).fill(false));

  // Tab workflows
  const [activeWorkflowTab, setActiveWorkflowTab] = useState(0);

  // Modal states for updating screening status
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(today);
  const [modalStatus, setModalStatus] = useState("");
  const [modalFacility, setModalFacility] = useState("");
  const [modalVerified, setModalVerified] = useState(false);
  const [modalErrors, setModalErrors] = useState({});
  const [screeningStatus, setScreeningStatus] = useState("");
  const [followupHistory, setFollowupHistory] = useState([]);
  const [medicines, setMedicines] = useState([
    { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }
  ]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [medicineSearchText, setMedicineSearchText] = useState('');
  const [encounterId, setEncounterId] = useState(null);
  const [isDiagnosisSaved, setIsDiagnosisSaved] = useState(false);

  const statusOptions = [
    { value: "Treatement", label: "Treatement" },
    { value: "Diagnosed", label: "Diagnosed" }
  ];

  const facilityOptions = [
    { value: "CHC", label: "CHC" },
    { value: "DH", label: "DH" },
    { value: "Tertiary", label: "Tertiary" }
  ];


  const validateModalForm = () => {
    const errors = {};
    if (!modalDate) {
      errors.date = "Date is required";
    }
    if (!modalStatus) {
      errors.status = "Status is required";
    }
    if (!modalFacility) {
      errors.facility = "Facility is required";
    }
    setModalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveModalStatus = async () => {
    if (!validateModalForm()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const payload = {
      changeFacility: modalFacility,
      changeStatus: modalStatus,
      statusVerified: modalVerified,
      consultationDate: formatToApiDate(modalDate)
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.updateSreeningStatus(300, individualId, cervicalScreeningId, payload);
      if (response && response.success) {
        setScreeningStatus(modalStatus);
        toast.success(`Screening status updated to "${modalStatus}" successfully!`);
        setIsStatusModalOpen(false);
      } else {
        toast.error(response?.message || "Failed to update screening status");
      }
    } catch (error) {
      console.error("updateSreeningStatus error:", error);
      toast.error(error.response?.data?.message || "Something went wrong while updating screening status");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOpenUpdateModal = () => {
    setModalDate(formData.assessmentDate || today);
    setModalStatus(screeningStatus);
    setModalFacility("");
    setModalVerified(false);
    setModalErrors({});
    setIsStatusModalOpen(true);
  };

  // Diagnosis form fields
  const [diagnosisAction, setDiagnosisAction] = useState("Refer");
  const [doctorsObservation, setDoctorsObservation] = useState("");
  const [isDiagnosed, setIsDiagnosed] = useState(false);
  const [patientDiagnosis, setPatientDiagnosis] = useState("");
  const [isNad, setIsNad] = useState(false);
  const [cryotherapyRecommended, setCryotherapyRecommended] = useState(false);
  const [referralRecommended, setReferralRecommended] = useState(false);
  const [managementRecommended, setManagementRecommended] = useState(false);
  const [isActionAlertDialogOpen, setIsActionAlertDialogOpen] = useState(false);
  const [actionTakenTitle, setActionTakenTitle] = useState("Action to be taken");
  const [actionTakenText, setActionTakenText] = useState("");

  const [dhDiagnosisData, setDhDiagnosisData] = useState({
    typeOfDiagnosis: '',
    diagnosis: '',
    diagnosisOther: '',
    diagnosisCancerType: '',
    diagnosisCancerTypeOther: '',
    cancerStage: null
  });
  const [dhErrors, setDhErrors] = useState({});

  // Review / Follow Up form fields
  const [reviewObservations, setReviewObservations] = useState("");
  const [referralRemarks, setReferralRemarks] = useState("");
  const [treatmentType, setTreatmentType] = useState("");
  const [treatmentOther, setTreatmentOther] = useState("");

  // DH / CHC Diagnosis state & facility check
  const facilityType = loggedData.facilityType || (loggedData.facilityTypeId === 3400 ? "DH" : loggedData.facilityTypeId === 3300 ? "CHC" : "PHC");
  const isDhOrChc = facilityType === "DH" || facilityType === "CHC" || loggedData.facilityTypeId === 3400 || loggedData.facilityTypeId === 3300;

  // Follow Up form fields
  const [followupReason, setFollowupReason] = useState("");
  const [followupTo, setFollowupTo] = useState("");
  const [durationDays, setDurationDays] = useState("0");
  const [durationWeeks, setDurationWeeks] = useState("0");
  const [durationMonths, setDurationMonths] = useState("0");
  const [dueDate, setDueDate] = useState("");
  const [errors, setErrors] = useState({});

  // Refer form fields
  const [referDate, setReferDate] = useState(today);
  const [referTo, setReferTo] = useState("");
  const [referReason, setReferReason] = useState("");
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState("");
  const [referredSpecialty, setReferredSpecialty] = useState("");
  const [seekingPrivateCare, setSeekingPrivateCare] = useState("");

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [formData, setFormData] = useState({
    assessmentDate: today,
    noAbnormalityFound: false,
    isExternalGenitaliaHealthy: null,
    polyp: null,
    ectopy: null,
    hypertrophy: null,
    excessiveDischarge: null,
    prolapseUterus: null,
    bleedsOnTouch: null,
    unhealthyCervix: null,
    suspiciousCervix: null,
    frankMalignancy: null,
    other: null,
    otherStr: '',
    byAnnualExamination: '',
    otherFindingsByannual: null,
    otherFindingsByAnnualRemarks: '',
    viaTestResult: '',
    viaPositivePoints: [],
  });

  const calculateDueDate = (days, weeks, months) => {
    const baseDateStr = formData?.assessmentDate || cervicalData?.data?.exam?.assessmentDate;
    let date = baseDateStr ? dayjs(baseDateStr, ['DD-MM-YYYY', 'YYYY-MM-DD']) : dayjs();
    if (!date.isValid()) date = dayjs();
    if (days && !isNaN(days)) date = date.add(parseInt(days), 'day');
    if (weeks && !isNaN(weeks)) date = date.add(parseInt(weeks) * 7, 'day');
    if (months && !isNaN(months)) date = date.add(parseInt(months), 'month');
    return date.format('DD-MM-YYYY');
  };

  const handleDurationChange = (type, val) => {
    let days = "0";
    let weeks = "0";
    let months = "0";
    if (type === 'days') {
      days = val;
      setDurationDays(val);
      setDurationWeeks("0");
      setDurationMonths("0");
    } else if (type === 'weeks') {
      weeks = val;
      setDurationWeeks(val);
      setDurationDays("0");
      setDurationMonths("0");
    } else if (type === 'months') {
      months = val;
      setDurationMonths(val);
      setDurationDays("0");
      setDurationWeeks("0");
    }

    // Clear errors for the modified field
    if (errors.duration || errors[`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]) {
      setErrors(prev => ({
        ...prev,
        duration: null,
        [`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]: null
      }));
    }

    const d = parseInt(days) || 0;
    const w = parseInt(weeks) || 0;
    const m = parseInt(months) || 0;

    if (d === 0 && w === 0 && m === 0) {
      setDueDate("");
    } else {
      const calculated = calculateDueDate(days, weeks, months);
      setDueDate(calculated);
      if (errors.dueDate) {
        setErrors(prev => ({ ...prev, dueDate: null }));
      }
    }
  };

  const validateFollowupForm = () => {
    const newErrors = {};
    if (!followupReason || !followupReason.trim()) {
      newErrors.followupReason = "Reason for Follow-up is required";
    }
    if (!followupTo || followupTo === "" || followupTo === "None") {
      newErrors.followupTo = "Follow-up facility is required";
    }

    const days = parseInt(durationDays) || 0;
    const weeks = parseInt(durationWeeks) || 0;
    const months = parseInt(durationMonths) || 0;

    if (days < 0) newErrors.durationDays = "Days cannot be negative";
    if (weeks < 0) newErrors.durationWeeks = "Weeks cannot be negative";
    if (months < 0) newErrors.durationMonths = "Months cannot be negative";

    if (days === 0 && weeks === 0 && months === 0) {
      newErrors.duration = "At least one duration (days/weeks/months) is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateReferralForm = () => {
    const newErrors = {};
    if (!referTo || referTo === "") {
      newErrors.referTo = "Referred To is required";
    }
    if (!referReason || referReason === "") {
      newErrors.referReason = "Reason for Referral is required";
    }
    if (!referredSpecialty || referredSpecialty === "") {
      newErrors.referredSpecialty = "Referred Specialty is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsPageLoading(true);
        const responseData = await ncdAPI.getCervicalScreening(individualId, cervicalScreeningId);
        setCervicalData(responseData);
        const hasDiag = responseData?.optedToReview || responseData?.optedToRefer || responseData?.optedForManagement || responseData?.data?.optedToReview || responseData?.data?.optedToRefer || responseData?.data?.optedForManagement;
        if (hasDiag) {
          setIsDiagnosisSaved(true);
        }

        const respaction = await ncdAPI.actionDetails(
          300,
          individualId,
          cervicalScreeningId
        );

        if (respaction?.data?.curActName) {
          setScreeningStatus(respaction?.data?.curActName);
        }

        const fetchedData = responseData?.data || responseData;

        if (fetchedData && fetchedData.exam) {
          if (fetchedData.exam.viaExam && fetchedData.exam.viaExam.viaPositivePoints) {
            const updatedPoints = Array(12).fill(false);
            fetchedData.exam.viaExam.viaPositivePoints.forEach((point) => {
              const match = point.match(/(CPS|CNS|CCS)(\d+)/);
              if (match) {
                const index = parseInt(match[2], 10) - 1; // Convert CPS1 to index 0
                if (index >= 0 && index < 12) {
                  updatedPoints[index] = true;
                }
              }
            });

            setActivePoints(updatedPoints);
            setPoints(fetchedData.exam.viaExam.viaPositivePoints);
          }

          setFormData((prev) => ({
            ...prev,
            assessmentDate: formatToInputDate(fetchedData.assessmentDate) || today,
            noAbnormalityFound: fetchedData.exam.noAbnormalityFound || false,
            viaTestResult: fetchedData.exam.viaExam?.viaTestResult || '',
            viaPositivePoints: fetchedData.exam.viaExam?.viaPositivePoints || [],
            isExternalGenitaliaHealthy: fetchedData.exam.isExternalGenitaliaHealthy,
            polyp: fetchedData.exam.polyp,
            ectopy: fetchedData.exam.ectopy,
            hypertrophy: fetchedData.exam.hypertrophy,
            excessiveDischarge: fetchedData.exam.excessiveDischarge,
            prolapseUterus: fetchedData.exam.prolapseUterus,
            bleedsOnTouch: fetchedData.exam.bleedsOnTouch,
            unhealthyCervix: fetchedData.exam.unhealthyCervix,
            suspiciousCervix: fetchedData.exam.suspiciousCervix,
            frankMalignancy: fetchedData.exam.frankMalignancy,
            other: fetchedData.exam.other,
            otherStr: fetchedData.exam.otherStr || '',
            byAnnualExamination: fetchedData.exam.byAnnualExamination || '',
            otherFindingsByannual: fetchedData.exam.otherFindingsByannual,
            otherFindingsByAnnualRemarks: fetchedData.exam.otherFindingsByAnnualRemarks || '',
          }));
        }

        if (fetchedData && fetchedData.diagnosis) {
          setDhDiagnosisData({
            typeOfDiagnosis: fetchedData.diagnosis.provisionalDiagnosis ? 'Provisional' : 'Confirmed',
            diagnosis: fetchedData.diagnosis.diagnosisType || '',
            diagnosisOther: fetchedData.diagnosis.diagnosisOther || '',
            cancerStage: fetchedData.diagnosis.cancerStage || null
          });
        }

        // Fetch follow-up history
        try {
          const historyResponse = await ncdAPI.getScreeningFollowup(300, individualId, cervicalScreeningId);
          if (historyResponse) {
            setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
          }
        } catch (error) {
          console.error("Failed to fetch follow-up history:", error);
        }

        // Fetch screening treatment to load existing medicines
        try {
          const treatmentResponse = await ncdAPI.getScreeningTreatment(300, individualId, cervicalScreeningId);
          if (treatmentResponse && treatmentResponse.success && treatmentResponse.data) {
            const medicalTreatment = treatmentResponse.data.treatment && Array.isArray(treatmentResponse.data.treatment)
                ? treatmentResponse.data.treatment.find(t => Array.isArray(t.medicineMangementMedList) && t.medicineMangementMedList.length > 0)
                : null;
            const savedMeds = (treatmentResponse.data.medicines && treatmentResponse.data.medicines.length > 0)
                ? treatmentResponse.data.medicines
                : (medicalTreatment ? medicalTreatment.medicineMangementMedList : []);

            if (savedMeds && savedMeds.length > 0) {
              setMedicines(savedMeds.map(med => ({
                medicine: med.name || med.drug || '',
                frequency: med.freq || med.frequency || '',
                duration: med.dur !== undefined && med.dur !== null ? String(med.dur) : (med.durationDays || med.duration || ''),
                quantity: med.quan !== undefined && med.quan !== null ? String(med.quan) : (med.quantity || ''),
                instruction: med.spInst || med.specialInstruction || med.instruction || ''
              })));
            }
          }
        } catch (error) {
          console.error("Failed to fetch screening treatment:", error);
        }

        setIsPageLoading(false);
      } catch (err) {
        setIsPageLoading(false);
        console.error("Failed to fetch and populate form data", err);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cervicalScreeningId, individualId]);

  useEffect(() => {
    if (activeWorkflowTab === 1 && individualId && cervicalScreeningId) {
      const fetchDiagAndAction = async () => {
        try {
          const respDiag = await ncdAPI.getScreeningDiagnosis(300, individualId, cervicalScreeningId);
          if (respDiag && (respDiag.success || respDiag.data || respDiag.diagnosis)) {
            const diagData = respDiag.data || respDiag;
            const diagnosed = !!diagData.isDiagnosed;
            setIsDiagnosed(diagnosed);

            const isNadFlag = !!diagData.isNad;
            const cryoRecFlag = !!diagData.cryotherapyRecommended;
            const referralRecFlag = !!diagData.referralRecommended;
            const managementRecFlag = !!diagData.managementRecommended;

            setIsNad(isNadFlag);
            setCryotherapyRecommended(cryoRecFlag);
            setReferralRecommended(referralRecFlag);
            setManagementRecommended(managementRecFlag);

            if (diagData.diagnosisRecommendationText) {
              setActionTakenTitle("Action to be taken");
              setActionTakenText(diagData.diagnosisRecommendationText);
              setIsActionAlertDialogOpen(true);
            }

            if (diagData.docObservations) {
              setDoctorsObservation(diagData.docObservations);
            }

            if (!isDhOrChc) {
              if (!diagnosed) {
                if (isNadFlag) {
                  setDiagnosisAction("NAD");
                } else if (cryoRecFlag) {
                  setDiagnosisAction("CRYOTHERAPY");
                } else if (referralRecFlag) {
                  setDiagnosisAction("Refer");
                } else if (managementRecFlag) {
                  setDiagnosisAction("Management");
                }
              } else {
                setDiagnosisAction("FollowUp");
                if (diagData.diagnosis) {
                  setPatientDiagnosis(typeof diagData.diagnosis === 'string' ? diagData.diagnosis : (diagData.diagnosis.diagnosis || diagData.diagnosis.diagnosisType || ''));
                }
              }
            } else if (diagData.diagnosis) {
              const diag = diagData.diagnosis;
              setDhDiagnosisData({
                typeOfDiagnosis: diag.typeOfDiagnosis || (diag.provisionalDiagnosis === true ? 'Provisional' : diag.provisionalDiagnosis === false ? 'Confirmed' : 'Provisional'),
                diagnosis: diag.diagnosisType || diag.diagnosis || '',
                diagnosisOther: diag.diagnosisOther || '',
                cancerStage: diag.cancerStage || null
              });
            }
          }
        } catch (err) {
          console.error("Failed to fetch cervical screening diagnosis:", err);
        }

        try {
          const respAction = await ncdAPI.actionDetails(300, individualId, cervicalScreeningId);
          if (respAction?.data?.curActName) {
            setScreeningStatus(respAction.data.curActName);
          }
        } catch (err) {
          console.error("Failed to fetch action details:", err);
        }
      };

      fetchDiagAndAction();
    }
  }, [activeWorkflowTab, individualId, cervicalScreeningId, isDhOrChc]);

  useEffect(() => {
    const fetchMedicineOptions = async () => {
      if (medicineSearchText && medicineSearchText.length >= 1) {
        try {
          const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
          if (response && response.data) {
            setMedicineOptions(response.data);
          }
        } catch (error) {
          console.error('Error fetching medicine options:', error);
        }
      } else {
        setMedicineOptions([]);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchMedicineOptions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [medicineSearchText]);

  useEffect(() => {
    const frequencyMap = {
      'Once a day': 1,
      'Twice a day': 2,
      'Thrice a day': 3,
      'Four times a day': 4
    };

    let updated = false;
    const newMedicines = medicines.map(med => {
      if (med.frequency && med.duration) {
        const freqValue = frequencyMap[med.frequency] || 0;
        const durationValue = parseInt(med.duration) || 0;
        const calculatedQuantity = freqValue * durationValue;

        if (calculatedQuantity > 0 && med.quantity !== String(calculatedQuantity)) {
          updated = true;
          return { ...med, quantity: String(calculatedQuantity) };
        }
      } else if (med.quantity !== '') {
        updated = true;
        return { ...med, quantity: '' };
      }
      return med;
    });

    if (updated) {
      setMedicines(newMedicines);
    }
  }, [medicines]);

  const handleMedicineFieldChange = (index, field, value) => {
    const updated = [...medicines];
    updated[index][field] = value;
    setMedicines(updated);
  };

  const addMedicineRow = () => {
    setMedicines([...medicines, { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
  };

  const removeMedicineRow = (index) => {
    if (medicines.length === 1) {
      setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
    } else {
      setMedicines(medicines.filter((_, i) => i !== index));
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === 'radio' && (value === 'Yes' || value === 'No')) {
      setFormData({
        ...formData,
        [name]: value === 'Yes',
      });
    } else if (type === 'radio' && name === 'cirvical') {
      setActivePoints(Array(12).fill(false));
      setPoints([]);
      setFormData({
        ...formData,
        viaTestResult: value,
        viaPositivePoints: [],
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleConfirmNo = () => {
    setOpenConfirmDialog(false);
    setFormData(prev => ({
      ...prev,
      noAbnormalityFound: false
    }));
  };

  const handleConfirmYes = () => {
    setOpenConfirmDialog(false);
    setActivePoints(Array(12).fill(false));
    setPoints([]);
    setFormData(prev => ({
      ...prev,
      noAbnormalityFound: true,
      isExternalGenitaliaHealthy: null,
      polyp: null,
      ectopy: null,
      hypertrophy: null,
      excessiveDischarge: null,
      prolapseUterus: null,
      bleedsOnTouch: null,
      unhealthyCervix: null,
      suspiciousCervix: null,
      frankMalignancy: null,
      other: null,
      otherStr: '',
      byAnnualExamination: '',
      otherFindingsByannual: null,
      otherFindingsByAnnualRemarks: '',
      viaTestResult: '',
      viaPositivePoints: [],
    }));
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      assessmentDate: formatToApiDate(formData.assessmentDate),
      exam: {
        abnormalPatches: false,
        noAbnormalityFound: formData.noAbnormalityFound,
        examDate: formatToApiDate(formData.assessmentDate),
        isExternalGenitaliaHealthy: formData.isExternalGenitaliaHealthy,
        polyp: formData.polyp,
        ectopy: formData.ectopy,
        hypertrophy: formData.hypertrophy,
        excessiveDischarge: formData.excessiveDischarge,
        prolapseUterus: formData.prolapseUterus,
        bleedsOnTouch: formData.bleedsOnTouch,
        unhealthyCervix: formData.unhealthyCervix,
        suspiciousCervix: formData.suspiciousCervix,
        frankMalignancy: formData.frankMalignancy,
        other: formData.other,
        otherStr: formData.otherStr || null,
        byAnnualExamination: formData.byAnnualExamination || null,
        otherFindingsByannual: formData.otherFindingsByannual,
        otherFindingsByAnnualRemarks: formData.otherFindingsByAnnualRemarks || null,
        viaExam: (formData.viaTestResult && formData.viaPositivePoints.length > 0)
          ? {
            viaTestResult: formData.viaTestResult,
            viaPositivePoints: formData.viaPositivePoints
          }
          : {}
      }
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.submitCervicalScreening(individualId, cervicalScreeningId, payload);

      if (response.success) {
        toast.success(`Cervical cancer screening data has saved successfully`);
        const newEncounterId = response.data?.encounterId;
        if (newEncounterId) {
          setEncounterId(newEncounterId);
        }
        setActiveWorkflowTab(1);
        setIsPageLoading(false);
      } else {
        toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
        setIsPageLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
      setIsPageLoading(false);
    }
  };

  const handleSaveFollowup = async () => {
    if (!validateFollowupForm()) {
      toast.error("Please fill in the mandatory fields correctly.");
      return;
    }

    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
    const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
    const toFacility = Number(followupTo) || FACILITY_MAP[followupTo] || fromFacility;

    const followUpPayload = {
      followUpDueDate: dueDate ? (dayjs(dueDate, 'DD-MM-YYYY', true).isValid() ? dueDate : dayjs(dueDate).format('DD-MM-YYYY')) : '',
      followUpDuration: `${durationDays || 0};${durationWeeks || 0};${durationMonths || 0}`,
      followUpReason: followupReason,
      fromFacility: fromFacility,
      toFacility: toFacility,
      type: "E",
      fromUser: loggedData.username || localStorage.getItem("ncd_username") || "doc_1220004",
      encounterId: encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId || null
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.submitCervicalFollowup(individualId, cervicalScreeningId, followUpPayload);
      if (response && response.success) {
        toast.success(response.message || "Follow-up details saved successfully!");
        
        // Fetch updated history
        try {
          const historyResponse = await ncdAPI.getScreeningFollowup(300, individualId, cervicalScreeningId);
          if (historyResponse) {
            setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
          }
        } catch (error) {
          console.error("Failed to fetch updated follow-up history:", error);
        }
        setActiveWorkflowTab(0); // return to main tab
        // Reset form fields
        setFollowupReason("");
        setFollowupTo("");
        setDurationDays("0");
        setDurationWeeks("0");
        setDurationMonths("0");
        setDueDate("");
        setErrors({});
      } else {
        toast.error(response?.message || "Failed to save follow-up details", { className: "custom-toast-error" });
      }
    } catch (error) {
      console.error("submitCervicalFollowup error:", error);
      toast.error(error.response?.data?.message || "Something went wrong while saving follow-up details", { className: "custom-toast-error" });
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleSaveReferral = async () => {
    if (!validateReferralForm()) {
      toast.error("Please fill in the mandatory Referral fields (*)");
      return;
    }

    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
    const referredFromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
    const referredToFacility = referTo ? (FACILITY_MAP[referTo] || String(referTo)) : "3400";

    const payload = {
      referredDate: referDate ? dayjs(referDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : '',
      referredToFacility: referredToFacility,
      referredFromFacility: referredFromFacility,
      referralReason: referReason,
      referredSpeciality: referredSpecialty,
      privateHospitalCare: seekingPrivateCare === "Yes",
      provisionalDiagnosis: provisionalDiagnosis || null,
      encounterId: null
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.submitCervicalRefer(individualId, cervicalScreeningId, payload);
      if (response && response.success) {
        toast.success(response.message || "Referral details saved successfully!");
        setActiveWorkflowTab(0); // return to main tab
        // Reset form fields
        setReferTo("");
        setReferReason("");
        setProvisionalDiagnosis("");
        setReferredSpecialty("");
        setSeekingPrivateCare("");
        setErrors({});
      } else {
        toast.error(response?.message || "Failed to save referral details", { className: "custom-toast-error" });
      }
    } catch (error) {
      console.error("submitCervicalRefer error:", error);
      toast.error(error.response?.data?.message || "Something went wrong while saving referral details", { className: "custom-toast-error" });
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleSaveDiagnosis = async () => {
    if (isDhOrChc) {
      const newErrors = {};
      if (!dhDiagnosisData.typeOfDiagnosis) {
        newErrors.typeOfDiagnosis = "This field is required.";
      }
      if (!dhDiagnosisData.diagnosis) {
        newErrors.diagnosis = "This field is required.";
      }
      if (Object.keys(newErrors).length > 0) {
        setDhErrors(newErrors);
        toast.error("Please fill all required fields");
        return;
      }
      setDhErrors({});
    }

    const payload = isDhOrChc ? {
      assessmentDate: formatToApiDate(formData.assessmentDate),
      optedToReview: null,
      optedToRefer: null,
      optedForCryotherapy: null,
      diagnosis: {
        diagnosisDate: formatToApiDate(formData.assessmentDate),
        provisionalDiagnosis: dhDiagnosisData.typeOfDiagnosis === 'Provisional',
        diagnosisType: dhDiagnosisData.diagnosis,
        diagnosisOther: dhDiagnosisData.diagnosisOther || null,
        cancerStage: dhDiagnosisData.cancerStage || null
      },
      docObservations: doctorsObservation || null,
      encounterId: encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId || null
    } : {
      assessmentDate: formatToApiDate(formData.assessmentDate),
      optedToReview: diagnosisAction === "Review" ? true : null,
      optedToRefer: diagnosisAction === "Refer" ? true : null,
      optedForManagement: diagnosisAction === "Management" ? true : null,
      diagnosis: null,
      docObservations: doctorsObservation || null,
      encounterId: encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId || null
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.submitScreeningDiagnosis(300, individualId, cervicalScreeningId, payload);
      if (response && response.success) {
        toast.success("Diagnosis details saved successfully!");
        setIsDiagnosisSaved(true);
        setActiveWorkflowTab(2); // proceed to Review / Follow Up
      } else {
        toast.error(response?.message || "Failed to save diagnosis details");
      }
    } catch (error) {
      console.error("submitScreeningDiagnosis error:", error);
      toast.error(error.response?.data?.message || "Something went wrong while saving diagnosis details");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleSaveReview = async (customPayload) => {
    const activeEncounterId = encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId || null;

    if (isDhOrChc && customPayload) {
      if (!customPayload.encounterId) {
        customPayload.encounterId = activeEncounterId;
      }
      try {
        setIsPageLoading(true);
        const response = await ncdAPI.submitScreeningTreatment(300, individualId, cervicalScreeningId, customPayload);
        if (response && response.success) {
          toast.success(response.message || "Review details saved successfully!");
          setActiveWorkflowTab(0);
        } else {
          toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
        }
      } catch (error) {
        console.error("submitScreeningTreatment review error:", error);
        toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
      } finally {
        setIsPageLoading(false);
      }
      return;
    }

    if (referTo || referReason) {
      if (!referTo) {
        toast.error("Please select Refer to facility");
        return;
      }
      if (!referReason) {
        toast.error("Please select Reason for Referral");
        return;
      }
    }

    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
    const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
    const referredToFacility = Number(referTo) || FACILITY_MAP[referTo] || 3400;

    const referPayload = (referTo || referReason) ? {
      referredDate: dayjs(formData.assessmentDate).format('DD-MM-YYYY'),
      referredToFacility: referredToFacility,
      referredFromFacility: fromFacility,
      referralReason: referReason,
      referredSpeciality: referredSpecialty || "Medical",
      privateHospitalCare: seekingPrivateCare === "Yes",
      provisionalDiagnosis: reviewObservations || referralRemarks || null,
      encounterId: activeEncounterId
    } : null;

    const payload = {
      assessmentDate: formatToApiDate(formData.assessmentDate),
      encounterId: activeEncounterId,
      treatment: null,
      refer: referPayload,
      followUp: null,
      reviewComments: reviewObservations || null,
      medicines: medicines.filter(med => med.medicine).length > 0 ? medicines.filter(med => med.medicine).map(med => ({
        conditn: "Cervical Cancer",
        name: med.medicine,
        prscrbdStrngth: null,
        freq: med.frequency,
        quan: Number(med.quantity) || null,
        dur: Number(med.duration) || null,
        durType: null,
        spInst: med.instruction
      })) : null
    };

    try {
      setIsPageLoading(true);
      const response = await ncdAPI.submitScreeningTreatment(300, individualId, cervicalScreeningId, payload);
      if (response && response.success) {
        toast.success(response.message || "Review details saved successfully!");
        setActiveWorkflowTab(0); // return to main tab
        setReferTo("");
        setReferReason("");
        setReferralRemarks("");
        setReviewObservations("");
        setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
      } else {
        toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
      }
    } catch (error) {
      console.error("submitScreeningTreatment review error:", error);
      toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
    } finally {
      setIsPageLoading(false);
    }
  };


  const togglePoint = (index) => {
    if (formData.noAbnormalityFound) return;
    if (formData.viaTestResult !== "Positive" && formData.viaTestResult !== "Suspicious") return;
    const updatedPoints = [...activePoints];
    updatedPoints[index] = !updatedPoints[index];
    setActivePoints(updatedPoints);

    const selectedValue = formData.viaTestResult;

    let prefix = "CPS"; // default
    if (selectedValue === "Negative") prefix = "CNS";
    else if (selectedValue === "Suspicious") prefix = "CPS";

    const pointLabels = updatedPoints
      .map((isActive, i) => isActive ? `${prefix}${i + 1}` : null)
      .filter(Boolean);

    setPoints(pointLabels);

    // Update formData with new selected points
    setFormData((prev) => ({
      ...prev,
      viaPositivePoints: pointLabels,
    }));
  };

  return (
    <>
      {isPageLoading && <Loader />}

      {/* Top Tab Switchers */}
      <Box sx={{ borderBottom: '1px solid #cbd5e1', bgcolor: '#f1f5f9', px: 2, display: 'flex', gap: 4, mb: 1 }}>
        {[
          { id: 0, label: "Examination" },
          { id: 1, label: "Diagnosis" },
          { id: 2, label: "Review / Follow Up" },
          ...(activeWorkflowTab === 3 ? [{ id: 3, label: "Follow Up" }] : []),
          ...(activeWorkflowTab === 4 ? [{ id: 4, label: "Refer" }] : [])
        ].map((tab) => (
          <Box
            key={tab.id}
            onClick={() => {
              if (tab.id === 0) {
                setActiveWorkflowTab(0);
              } else if (tab.id === 1) {
                const hasEncounter = encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId;
                if (hasEncounter) {
                  setActiveWorkflowTab(1);
                } else {
                  toast.error("Please submit the Examination details first.");
                }
              } else if (tab.id === 2) {
                const hasEncounter = encounterId || cervicalData?.encounterId || cervicalData?.data?.encounterId;
                const hasDiag = isDiagnosisSaved || cervicalData?.optedToReview || cervicalData?.optedToRefer || cervicalData?.optedForManagement || cervicalData?.data?.optedToReview || cervicalData?.data?.optedToRefer || cervicalData?.data?.optedForManagement;
                if (!hasEncounter) {
                  toast.error("Please submit the Examination details first.");
                } else if (!hasDiag) {
                  toast.error("Please submit the Diagnosis details first.");
                } else {
                  setActiveWorkflowTab(2);
                }
              } else {
                setActiveWorkflowTab(tab.id);
              }
            }}
            sx={{
              py: 1.8,
              px: 1,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 700,
              color: activeWorkflowTab === tab.id ? '#3f51b5' : '#64748b',
              borderBottom: activeWorkflowTab === tab.id ? '3px solid #3f51b5' : '3px solid transparent',
              transition: 'all 0.2s',
              '&:hover': { color: '#3f51b5' }
            }}
          >
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Render Modular Tab Subcomponents */}
      {activeWorkflowTab === 0 && (
        <CervicalExaminationTab
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleFieldChange={handleFieldChange}
          handleSubmit={handleSubmit}
          screeningStatus={screeningStatus}
          isDhOrChc={isDhOrChc}
          handleOpenUpdateModal={handleOpenUpdateModal}
          activeWorkflowTab={activeWorkflowTab}
          setActiveWorkflowTab={setActiveWorkflowTab}
          setOpenConfirmDialog={setOpenConfirmDialog}
          activePoints={activePoints}
          togglePoint={togglePoint}
        />
      )}

      {activeWorkflowTab === 1 && (
        <CervicalDiagnosisTab
          facilityType={facilityType}
          isDhOrChc={isDhOrChc}
          formData={formData}
          diagnosisAction={diagnosisAction}
          setDiagnosisAction={setDiagnosisAction}
          doctorsObservation={doctorsObservation}
          setDoctorsObservation={setDoctorsObservation}
          dhDiagnosisData={dhDiagnosisData}
          setDhDiagnosisData={setDhDiagnosisData}
          dhErrors={dhErrors}
          setDhErrors={setDhErrors}
          handleSaveDiagnosis={handleSaveDiagnosis}
          setActiveWorkflowTab={setActiveWorkflowTab}
          screeningStatus={screeningStatus}
          handleOpenUpdateModal={handleOpenUpdateModal}
          isDiagnosed={isDiagnosed}
          patientDiagnosis={patientDiagnosis}
          isNad={isNad}
          cryotherapyRecommended={cryotherapyRecommended}
          referralRecommended={referralRecommended}
          managementRecommended={managementRecommended}
        />
      )}

      {activeWorkflowTab === 2 && (
        <CervicalReviewTab
          facilityType={facilityType}
          isDhOrChc={isDhOrChc}
          individualId={individualId}
          cervicalScreeningId={cervicalScreeningId}
          conditionId={300}
          screeningStatus={screeningStatus}
          formData={formData}
          reviewObservations={reviewObservations}
          setReviewObservations={setReviewObservations}
          treatmentType={treatmentType}
          setTreatmentType={setTreatmentType}
          treatmentOther={treatmentOther}
          setTreatmentOther={setTreatmentOther}
          medicines={medicines}
          medicineOptions={medicineOptions}
          setMedicineSearchText={setMedicineSearchText}
          handleMedicineFieldChange={handleMedicineFieldChange}
          addMedicineRow={addMedicineRow}
          removeMedicineRow={removeMedicineRow}
          followupReason={followupReason}
          setFollowupReason={setFollowupReason}
          followupTo={followupTo}
          setFollowupTo={setFollowupTo}
          durationDays={durationDays}
          durationWeeks={durationWeeks}
          durationMonths={durationMonths}
          handleDurationChange={handleDurationChange}
          dueDate={dueDate}
          setDueDate={setDueDate}
          referTo={referTo}
          setReferTo={setReferTo}
          referReason={referReason}
          setReferReason={setReferReason}
          referralRemarks={referralRemarks}
          setReferralRemarks={setReferralRemarks}
          handleSaveReview={handleSaveReview}
          setActiveWorkflowTab={setActiveWorkflowTab}
        />
      )}

      {activeWorkflowTab === 3 && (
        <CervicalFollowupTab
          followupReason={followupReason}
          setFollowupReason={setFollowupReason}
          followupTo={followupTo}
          setFollowupTo={setFollowupTo}
          durationDays={durationDays}
          durationWeeks={durationWeeks}
          durationMonths={durationMonths}
          handleDurationChange={handleDurationChange}
          dueDate={dueDate}
          errors={errors}
          setErrors={setErrors}
          followupHistory={followupHistory}
          getFacilityLabel={getFacilityLabel}
          handleSaveFollowup={handleSaveFollowup}
          facilityType={loggedData.facilityType}
        />
      )}

      {activeWorkflowTab === 4 && (
        <CervicalReferTab
          referDate={referDate}
          setReferDate={setReferDate}
          referTo={referTo}
          setReferTo={setReferTo}
          referReason={referReason}
          setReferReason={setReferReason}
          provisionalDiagnosis={provisionalDiagnosis}
          setProvisionalDiagnosis={setProvisionalDiagnosis}
          referredSpecialty={referredSpecialty}
          setReferredSpecialty={setReferredSpecialty}
          seekingPrivateCare={seekingPrivateCare}
          setSeekingPrivateCare={setSeekingPrivateCare}
          errors={errors}
          setErrors={setErrors}
          handleSaveReferral={handleSaveReferral}
        />
      )}

      <div className="separator"></div>

      <Dialog
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
            p: 1.5,
            overflow: 'visible'
          }
        }}
      >
        {/* Dialog Title */}
        <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef2f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e3a8a', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '20px' }}>⚡</span> Update Screening Status
          </Typography>
          <IconButton
            onClick={() => setIsStatusModalOpen(false)}
            sx={{
              color: '#94a3b8',
              '&:hover': {
                bgcolor: '#fee2e2',
                color: '#ef4444'
              },
              transition: 'all 0.2s'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Grid container spacing={3}>
            {/* Date Field */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                    Date <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  <Tooltip title="Specify the date when this screening status takes effect" arrow placement="top">
                    <IconButton size="small" sx={{ p: 0.2, color: '#3f51b5' }}>
                      <InfoIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={modalDate ? dayjs(modalDate, 'YYYY-MM-DD') : null}
                    onChange={(newValue) => {
                      setModalDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                      if (modalErrors.date) {
                        setModalErrors(prev => ({ ...prev, date: null }));
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!modalErrors.date,
                        helperText: modalErrors.date,
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: '#fff',
                            '& fieldset': { borderColor: '#cbd5e1' },
                            '&:hover fieldset': { borderColor: '#94a3b8' },
                            '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>

            {/* Status Dropdown */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                  Update status to <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!modalErrors.status}>
                  <Select
                    value={modalStatus}
                    onChange={(e) => {
                      setModalStatus(e.target.value);
                      if (modalErrors.status) {
                        setModalErrors(prev => ({ ...prev, status: null }));
                      }
                    }}
                    displayEmpty
                    sx={{
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                    }}
                  >
                    <MenuItem value="" disabled>Select Status</MenuItem>
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {modalErrors.status && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {modalErrors.status}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Grid>

            {/* Facility Dropdown */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                  Facility <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" error={!!modalErrors.facility}>
                  <Select
                    value={modalFacility}
                    onChange={(e) => {
                      setModalFacility(e.target.value);
                      if (modalErrors.facility) {
                        setModalErrors(prev => ({ ...prev, facility: null }));
                      }
                    }}
                    displayEmpty
                    sx={{
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                    }}
                  >
                    <MenuItem value="" disabled>Select Facility</MenuItem>
                    {facilityOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {modalErrors.facility && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {modalErrors.facility}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Grid>

            {/* Verified Checkbox Card */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: modalVerified ? 'rgba(63, 81, 181, 0.04)' : '#f8fafc',
                  border: '1px dashed',
                  borderColor: modalVerified ? '#3f51b5' : '#cbd5e1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#3f51b5',
                    bgcolor: 'rgba(63, 81, 181, 0.02)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                    Verified
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                    Mark this status check as verified
                  </Typography>
                </Box>
                <Checkbox
                  checked={modalVerified}
                  onChange={(e) => setModalVerified(e.target.checked)}
                  sx={{
                    color: '#cbd5e1',
                    '&.Mui-checked': {
                      color: '#3f51b5',
                    },
                    transform: 'scale(1.1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #eef2f5', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={() => setIsStatusModalOpen(false)}
            variant="outlined"
            sx={{
              color: '#64748b',
              borderColor: '#cbd5e1',
              fontWeight: 700,
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f8fafc'
              },
              transition: 'all 0.2s'
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSaveModalStatus}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              px: 3.5,
              py: 1,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #303f9f 0%, #202b7b 100%)',
                boxShadow: '0 6px 16px rgba(63, 81, 181, 0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s'
            }}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmNo}
        PaperProps={{
          sx: {
            padding: '16px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '100%',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', p: 1, pb: 2 }}>Confirm</DialogTitle>
        <DialogContent sx={{ p: 1, pb: 2 }}>
          <Typography sx={{ fontSize: '15px', color: '#333', lineHeight: 1.5 }}>
            Abnormal findings, if any, will be cleared and the patient will be marked NAD. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleConfirmNo}
            variant="contained"
            sx={{
              backgroundColor: '#3f51b5',
              color: '#fff',
              fontWeight: 'bold',
              minWidth: '80px',
              '&:hover': {
                backgroundColor: '#303f9f',
              }
            }}
          >
            NO
          </Button>
          <Button
            onClick={handleConfirmYes}
            variant="contained"
            sx={{
              backgroundColor: '#3f51b5',
              color: '#fff',
              fontWeight: 'bold',
              minWidth: '80px',
              '&:hover': {
                backgroundColor: '#303f9f',
              }
            }}
          >
            YES
          </Button>
        </DialogActions>
      </Dialog>

      {/* General Action Alert Dialog */}
      <Dialog
        open={isActionAlertDialogOpen}
        onClose={() => setIsActionAlertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
          {actionTakenTitle}
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.6 }}>
            {actionTakenText}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsActionAlertDialogOpen(false)} variant="contained" sx={{ bgcolor: '#3f51b5', fontWeight: 700 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CervicalScreening;

